import * as p from '@clack/prompts'
import pc from 'picocolors'

import { runVercel } from './runner.js'
import type { AuthResult, VercelRunner } from './types.js'

const WHOAMI_TIMEOUT_MS = 30_000
/** Login polls a browser approval, so give it a long window. */
const LOGIN_TIMEOUT_MS = 300_000

/**
 * Non-triggering auth check. `vercel whoami` exits 0 + prints the username when
 * authenticated; a non-zero exit means not signed in. Never launches a login.
 */
export async function checkVercelAuth(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<AuthResult> {
  const result = await runVercel(runner, ['whoami'], {
    cwd,
    mode: 'capture',
    timeoutMs: WHOAMI_TIMEOUT_MS,
    env
  })

  if (result.success) {
    const username = result.stdout.trim().split('\n').pop()?.trim()
    return { authed: true, username: username || undefined }
  }

  return { authed: false, reason: result.timedOut ? 'timeout' : 'not-authed' }
}

/**
 * Ensure the user is authenticated with Vercel.
 * - If already signed in, returns immediately.
 * - If `VERCEL_TOKEN` is set but invalid, fails without launching interactive login.
 * - Otherwise runs `vercel login`, which prints a clickable link + verification
 *   code and polls until approved, then re-verifies with `whoami`.
 *
 * Owns its spinners around the `whoami` checks (on the npx runner the first
 * check also downloads the Vercel CLI) and stops them before `vercel login`,
 * which inherits the TTY and would fight a running spinner animation. Callers
 * must not have their own spinner active when calling this.
 */
export async function ensureVercelAuth(
  runner: VercelRunner,
  cwd: string,
  options: { env?: NodeJS.ProcessEnv }
): Promise<AuthResult> {
  const env = options.env ?? process.env

  const spinner = p.spinner()
  spinner.start(
    runner.source === 'npx'
      ? 'Checking Vercel sign-in (first run downloads the Vercel CLI)'
      : 'Checking Vercel sign-in'
  )
  const existing = await checkVercelAuth(runner, cwd, env)
  if (existing.authed) {
    spinner.stop(signedInMessage(existing.username))
    return existing
  }

  // A token was supplied but whoami failed → token is invalid/expired. Do not
  // drop into an interactive browser login in what may be a headless run.
  if (env.VERCEL_TOKEN) {
    spinner.stop('Could not verify VERCEL_TOKEN with Vercel')
    return { authed: false, reason: 'login-failed' }
  }

  spinner.stop('Not signed in to Vercel yet')

  p.log.info(
    `Sign in to Vercel to continue — a browser link and code will appear below. ${pc.dim(
      '(or press Ctrl-C to enter a connection string manually)'
    )}`
  )

  const login = await runVercel(runner, ['login'], {
    cwd,
    mode: 'inherit',
    timeoutMs: LOGIN_TIMEOUT_MS,
    env
  })

  if (!login.success) {
    return { authed: false, reason: login.timedOut ? 'timeout' : 'login-cancelled' }
  }

  const verifySpinner = p.spinner()
  verifySpinner.start('Verifying Vercel sign-in')
  const after = await checkVercelAuth(runner, cwd, env)
  if (after.authed) {
    verifySpinner.stop(signedInMessage(after.username))
    return after
  }
  verifySpinner.stop('Could not verify your Vercel sign-in')
  return { authed: false, reason: 'login-failed' }
}

function signedInMessage(username?: string): string {
  return username ? `Signed in to Vercel as ${pc.cyan(username)}` : 'Signed in to Vercel'
}
